/**
 * WanT2VConfigPanel — parametrized configuration panel for local Wan providers.
 *
 * Supports three variants:
 *   - wan21_1_3b : T2V only, 8 GB VRAM min, 480p
 *   - wan22_5b   : T2V + I2V, 12 GB VRAM min, up to 720p
 *   - wan22_14b  : T2V only, 20 GB VRAM min (RTX 4090+), LightX2V Lightning
 *
 * Theme: dark-only. Uses bg-black/30, text-white/70, border-white/10.
 * No light-mode colors (memory: CSS Architecture rule).
 */

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FreeOnDesktopBadge } from '@/components/common/FreeOnDesktopBadge';
import { WanSpeedIndicator } from './WanSpeedIndicator';
import { WAN_VARIANT_SPECS, type WanVariant, type WanLocalConfig } from '@/constants/wanLocal';

export interface WanT2VConfigPanelProps {
  variant: WanVariant;
  value: WanLocalConfig;
  onChange: (v: WanLocalConfig) => void;
  /** VRAM in GB from useSystemGpuInfo(). null while loading. */
  vramGb: number | null;
  disabled?: boolean;
}

/** Parse "WxH" → { w, h } */
function parseResolution(res: string): { w: number; h: number } {
  const [w, h] = res.split('x').map(Number);
  return { w: w ?? 0, h: h ?? 0 };
}

/** Friendly label for a "WxH" resolution string. */
function resolutionLabel(res: string): string {
  const { w, h } = parseResolution(res);
  if (w === 1280 && h === 720) return '720p Landscape';
  if (w === 720 && h === 1280) return '720p Portrait';
  if (w === 832 && h === 480) return '480p Landscape';
  if (w === 480 && h === 832) return '480p Portrait';
  return `${w}x${h}`;
}

export const WanT2VConfigPanel: React.FC<WanT2VConfigPanelProps> = ({
  variant,
  value,
  onChange,
  vramGb,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const spec = WAN_VARIANT_SPECS[variant];

  const handleChange = useCallback(
    <K extends keyof WanLocalConfig>(key: K, newValue: WanLocalConfig[K]) => {
      onChange({ ...value, [key]: newValue });
    },
    [value, onChange]
  );

  const [stepsMin, stepsMax] = spec.stepsRange;
  const [fpsMin, fpsMax] = spec.fpsRange;
  const [guidanceMin, guidanceMax] = spec.guidanceRange;

  return (
    <div className="space-y-4 p-4 rounded-xl bg-black/30 border border-white/10">
      {/* Header row: variant name + Free badge */}
      <div className="flex items-center justify-between gap-2">
        <div>
          <span className="text-sm font-semibold text-white/90">{spec.displayName}</span>
          {variant === 'wan22_14b' && (
            <span className="ml-2 text-[10px] text-purple-300/80 font-medium">
              {t('wanLocal.tier.premium', { defaultValue: 'RTX 4090 24 GB+' })}
            </span>
          )}
        </div>
        <FreeOnDesktopBadge />
      </div>

      {/* Resolution */}
      <div className="space-y-1.5">
        <label className="block text-xs font-medium text-white/60 uppercase tracking-wide">
          {t('wanLocal.config.resolution', { defaultValue: 'Resolution' })}
        </label>
        <div className="flex flex-wrap gap-2">
          {spec.supportedResolutions.map((res) => (
            <button
              key={res}
              type="button"
              disabled={disabled}
              onClick={() => handleChange('resolution', res)}
              className={[
                'px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors',
                value.resolution === res
                  ? 'border-purple-400/60 bg-purple-500/20 text-white'
                  : 'border-white/10 bg-black/20 text-white/60 hover:border-white/30 hover:text-white/80',
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {resolutionLabel(res)}
            </button>
          ))}
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-white/60 uppercase tracking-wide">
            {t('wanLocal.config.steps', { defaultValue: 'Steps' })}
          </label>
          <span className="text-xs text-white/80 tabular-nums">{value.steps}</span>
        </div>
        <input
          type="range"
          min={stepsMin}
          max={stepsMax}
          step={1}
          value={value.steps}
          disabled={disabled}
          onChange={(e) => handleChange('steps', Number(e.target.value))}
          className="w-full accent-purple-400 disabled:opacity-50"
        />
        <p className="text-[10px] text-white/40">
          {t('wanLocal.config.stepsHelp', { defaultValue: 'More steps = better quality, slower' })}
        </p>
      </div>

      {/* FPS */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-white/60 uppercase tracking-wide">
            {t('wanLocal.config.fps', { defaultValue: 'FPS' })}
          </label>
          <span className="text-xs text-white/80 tabular-nums">{value.fps}</span>
        </div>
        <input
          type="range"
          min={fpsMin}
          max={fpsMax}
          step={1}
          value={value.fps}
          disabled={disabled}
          onChange={(e) => handleChange('fps', Number(e.target.value))}
          className="w-full accent-purple-400 disabled:opacity-50"
        />
      </div>

      {/* Frames */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-white/60 uppercase tracking-wide">
            {t('wanLocal.config.numFrames', { defaultValue: 'Frames' })}
          </label>
          <span className="text-xs text-white/80 tabular-nums">
            {value.num_frames}{' '}
            <span className="text-white/40">({(value.num_frames / value.fps).toFixed(1)}s)</span>
          </span>
        </div>
        <input
          type="range"
          min={1}
          max={spec.maxFrames}
          step={1}
          value={value.num_frames}
          disabled={disabled}
          onChange={(e) => handleChange('num_frames', Number(e.target.value))}
          className="w-full accent-purple-400 disabled:opacity-50"
        />
      </div>

      {/* Guidance Scale */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-white/60 uppercase tracking-wide">
            {t('wanLocal.config.guidance', { defaultValue: 'Guidance Scale' })}
          </label>
          <span className="text-xs text-white/80 tabular-nums">
            {value.guidance_scale.toFixed(1)}
          </span>
        </div>
        <input
          type="range"
          min={guidanceMin}
          max={guidanceMax}
          step={0.5}
          value={value.guidance_scale}
          disabled={disabled}
          onChange={(e) => handleChange('guidance_scale', Number(e.target.value))}
          className="w-full accent-purple-400 disabled:opacity-50"
        />
      </div>

      {/* Speed estimate */}
      <WanSpeedIndicator
        variant={variant}
        steps={value.steps}
        numFrames={value.num_frames}
        fps={value.fps}
        vramGb={vramGb}
      />

      {/* Cost footer */}
      <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-black/20 border border-white/5">
        <span className="text-xs text-white/50">
          {t('videoSource.cost', { defaultValue: 'Cost' })}
        </span>
        <span className="text-sm font-bold text-emerald-400">
          {t('common.free', { defaultValue: 'Free' })}
        </span>
      </div>
    </div>
  );
};

export default WanT2VConfigPanel;
